import { useCallback, useState } from 'react'

import { CategoryDetail } from '@/app/service/models/aiAssistants'
import { getCategoryDetails } from '@/app/service/experienceService'

interface UseCategoryDetailsReturn {
	categoryDetail: CategoryDetail | null
	loading: boolean
	error: string | null
	fetchCategoryDetails: (categoryName: string) => Promise<void>
}

/**
 * Custom hook to fetch AI assistant category details with questions
 *
 * @returns Object containing category details, loading state, error state, and fetch function
 */
export const useCategoryDetails = (): UseCategoryDetailsReturn => {
	const [categoryDetail, setCategoryDetail] = useState<CategoryDetail | null>(
		null
	)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)

	const fetchCategoryDetails = useCallback(async (categoryName: string) => {
		setLoading(true)
		setError(null)
		try {
			// Add 300ms delay to display loading spinner
			await new Promise<void>(resolve => {
				setTimeout(() => resolve(), 1000)
			})

			// Fetch category details using the service function
			const response = await getCategoryDetails(categoryName)

			// Find the specific category from the response
			const selectedCategory = response.category.find(
				cat => cat.name === categoryName
			)

			if (selectedCategory) {
				setCategoryDetail(selectedCategory)
			} else {
				setError(`No data found for category: ${categoryName}`)
				setCategoryDetail(null)
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to fetch category details'
			setError(errorMessage)
			setCategoryDetail(null)
		} finally {
			setLoading(false)
		}
	}, [])

	return {
		categoryDetail,
		loading,
		error,
		fetchCategoryDetails,
	}
}
